import React, { useId, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { isDarkMode } from "../../utils/constants";

const mutedColor = isDarkMode ? "rgba(245,247,250,0.67)" : "rgba(18,20,23,0.64)";
const linkColor = isDarkMode ? "#A8C1FF" : "#1D4ED8";

const normalizeSearchSources = (sources = []) =>
  Array.isArray(sources)
    ? sources.filter((source) => {
        if (!source || typeof source !== "object") return false;
        return Boolean(String(source.title || "").trim() && String(source.url || "").trim());
      })
    : [];

const getHostLabel = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
};

export const stripStructuredSearchSources = (text = "", rawSources = []) => {
  const sources = normalizeSearchSources(rawSources);
  if (!text || sources.length === 0) return text;

  const match = String(text).match(/\n+### Sources[\s\S]*$/);
  if (!match || match.index == null) return text;

  const tail = match[0];
  const hasStructuredUrl = sources.some((source) => tail.includes(source.url));
  if (!hasStructuredUrl) return text;

  return String(text)
    .slice(0, match.index)
    .replace(/\s+$/, "");
};

export const SearchUsageBadge = ({ sources = [] }) => {
  const sourceCount = normalizeSearchSources(sources).length;
  return (
    <Box
      data-search-usage
      sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75, mb: 1.5, color: mutedColor, fontSize: "11px", lineHeight: 1.5 }}
    >
      <PublicRoundedIcon aria-hidden="true" sx={{ fontSize: 14 }} />
      <span>Web searched</span>
      <span aria-hidden="true">·</span>
      <span>{sourceCount} {sourceCount === 1 ? "source" : "sources"}</span>
    </Box>
  );
};

export const SearchSourcesPanel = ({ sources = [] }) => {
  const [expanded, setExpanded] = useState(false);
  const listId = useId();
  const normalizedSources = normalizeSearchSources(sources);
  if (normalizedSources.length === 0) return null;

  return (
    <Box
      component="section"
      aria-label="Sources"
      data-search-sources
      sx={{ mt: 3, pt: 2, borderTop: `1px solid ${isDarkMode ? "rgba(235,240,250,0.12)" : "rgba(17,24,39,0.13)"}` }}
    >
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1, color: mutedColor }}>
        <Typography component="h2" sx={{ m: 0, fontSize: "12px", fontWeight: 500, lineHeight: 1.5, color: "inherit" }}>
          Sources
        </Typography>
        <Box component="span" sx={{ fontSize: "11px" }}>{normalizedSources.length}</Box>
      </Box>
      <Box component="ol" id={listId} sx={{ m: 0, p: 0, listStyle: "none" }}>
        {normalizedSources.map((source, index) => (
          <Box component="li" key={`${source.url}-${index}`} hidden={!expanded && index >= 1}>
            <Box
              component="a"
              href={source.url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "grid",
                gridTemplateColumns: "18px minmax(0, 1fr) 14px",
                alignItems: "start",
                gap: "9px",
                py: "9px",
                minWidth: 0,
                borderRadius: "4px",
                color: isDarkMode ? "#F5F7FA" : "#121417",
                textDecoration: "none",
                "&:hover": {
                  backgroundColor: isDarkMode ? "#1D2026" : "#F1F3F6",
                  "& [data-search-source-title]": { color: linkColor },
                },
                "&:focus-visible": {
                  outline: `2px solid ${linkColor}`,
                  outlineOffset: "3px",
                },
              }}
            >
              <Box component="span" aria-hidden="true" sx={{ color: mutedColor, fontSize: "11px", lineHeight: "19px", fontVariantNumeric: "tabular-nums" }}>{index + 1}</Box>
              <Box component="span" sx={{ minWidth: 0, whiteSpace: "normal", overflowWrap: "anywhere" }}>
                <Box component="span" data-search-source-title sx={{ display: "block", fontSize: "13px", fontWeight: 400, lineHeight: "19px" }}>
                  {source.title}
                </Box>
                <Box component="span" sx={{ display: "block", mt: "1px", color: mutedColor, fontSize: "11px", lineHeight: "17px" }}>
                  {getHostLabel(source.url) || source.url}
                </Box>
              </Box>
              <OpenInNewRoundedIcon aria-hidden="true" sx={{ mt: "2px", fontSize: 14, color: mutedColor }} />
            </Box>
          </Box>
        ))}
      </Box>
      {normalizedSources.length > 1 && (
        <Button
          type="button"
          aria-expanded={expanded}
          aria-controls={listId}
          onClick={() => setExpanded(value => !value)}
          endIcon={<ExpandMoreRoundedIcon sx={{ transform: expanded ? "rotate(180deg)" : "none" }} />}
          sx={{
            mt: 0.5,
            minHeight: 44,
            px: 1,
            color: linkColor,
            fontSize: "12px",
            fontWeight: 400,
            textTransform: "none",
            "&:focus-visible": { outline: `2px solid ${linkColor}`, outlineOffset: "2px" },
          }}
        >
          {expanded ? "Show fewer sources" : `Show all ${normalizedSources.length} sources`}
        </Button>
      )}
    </Box>
  );
};
